<template>
  <v-card
    class="plan-wizard"
    rounded="lg"
    elevation="2"
    width="100%"
    max-width="520"
  >
    <div class="plan-wizard__header text-white">
      <span>{{ t('marketing-agent.wizard.title') }}</span>
      <v-btn
        v-if="closable"
        icon="mdi-close"
        size="small"
        variant="text"
        color="white"
        @click="emit('close')"
      />
    </div>

    <div class="plan-wizard__body">
      <section
        v-if="connectionsError"
        class="plan-wizard__section"
      >
        <h3 class="plan-wizard__label">{{ t('marketing-agent.wizard.connect') }}</h3>
        <RetryNotice
          :message="t('marketing-agent.wizard.connections-error')"
          :state="connectionsRetry.state.value"
          :loading="retryingConnections"
          @retry="onRetryConnections"
        />
      </section>

      <section
        v-if="showConnections"
        class="plan-wizard__section"
      >
        <h3 class="plan-wizard__label">{{ t('marketing-agent.wizard.connect') }}</h3>
        <div
          class="plan-wizard__tiles"
          :class="{ 'plan-wizard__tiles--four': socialConnections.length > 3 }"
        >
          <SocialMediaWrapper
            v-for="connection in socialConnections"
            :key="connection.id"
            :provider="connection.provider"
            :connected-media="socialMediaConnections"
          >
            <template #activator="{ props: activatorProps, loading, disabled, connect }">
              <UpgradeInfoDialog
                v-if="connection.needsUpgrade"
                :show-info="true"
              >
                <template #default="{ props: upgradeProps }">
                  <button
                    type="button"
                    class="connect-tile"
                    v-bind="upgradeProps"
                  >
                    <img
                      class="connect-tile__logo"
                      :src="connection.image"
                      :alt="connection.id"
                    />
                    <span class="connect-tile__action">{{ t('labels.upgrade') }}</span>
                  </button>
                </template>
              </UpgradeInfoDialog>
              <v-tooltip
                v-else
                location="bottom"
                :disabled="!disabled || !connection.tooltip"
              >
                <template #activator="{ props: tooltipProps }">
                  <span
                    class="connect-tile-wrap"
                    v-bind="tooltipProps"
                  >
                    <button
                      type="button"
                      class="connect-tile"
                      :disabled="disabled || loading"
                      v-bind="activatorProps"
                      @click="connect"
                    >
                      <v-progress-circular
                        v-if="loading"
                        indeterminate
                        size="20"
                        width="2"
                        color="primary"
                      />
                      <img
                        v-else
                        class="connect-tile__logo"
                        :src="connection.image"
                        :alt="connection.id"
                      />
                      <span class="connect-tile__action">{{
                        t('marketing-agent.wizard.connect-action')
                      }}</span>
                    </button>
                  </span>
                </template>
                <span>{{ connection.tooltip }}</span>
              </v-tooltip>
            </template>
          </SocialMediaWrapper>
        </div>
      </section>

      <section class="plan-wizard__section">
        <h3 class="plan-wizard__label">{{ t('marketing-agent.wizard.topic') }}</h3>
        <v-select
          v-model="topicId"
          :items="topicItems"
          item-title="name"
          item-value="id"
          :label="t('marketing-agent.wizard.topic-label')"
          :loading="topicsLoading"
          :no-data-text="t('marketing-agent.empty.topics.text')"
          variant="outlined"
          density="comfortable"
          hide-details
          menu-icon="mdi-chevron-down"
        />
        <RetryNotice
          v-if="topicsError"
          class="mt-2"
          :message="t('marketing-agent.wizard.topics-error')"
          :state="topicsRetry.state.value"
          :loading="retryingTopics"
          @retry="onRetryTopics"
        />
        <p
          v-else-if="!topicsLoading && topicItems.length === 0"
          class="plan-wizard__hint"
        >
          {{ t('marketing-agent.empty.topics.text') }}
          <router-link to="/topics">{{ t('marketing-agent.empty.topics.cta') }}</router-link>
        </p>
      </section>

      <section
        v-if="showPlanDuration"
        class="plan-wizard__section"
      >
        <h3 class="plan-wizard__label">{{ t('marketing-agent.wizard.length') }}</h3>
        <v-select
          v-model="windowDays"
          :items="lengthItems"
          item-title="title"
          item-value="value"
          :label="t('marketing-agent.wizard.length')"
          variant="outlined"
          density="comfortable"
          hide-details
          menu-icon="mdi-chevron-down"
        />
      </section>

      <section class="plan-wizard__section">
        <h3 class="plan-wizard__label">{{ t('marketing-agent.wizard.date') }}</h3>
        <v-menu
          v-model="showDatePicker"
          :close-on-content-click="false"
          location="bottom"
          min-width="auto"
        >
          <template #activator="{ props: menuProps }">
            <v-text-field
              :model-value="formattedDate"
              :label="t('marketing-agent.wizard.date-label')"
              :placeholder="t('marketing-agent.wizard.date-label')"
              prepend-inner-icon="mdi-calendar"
              variant="outlined"
              density="comfortable"
              readonly
              hide-details
              v-bind="menuProps"
            />
          </template>
          <v-date-picker
            v-model="selectedDate"
            :min="minDate"
            @update:model-value="onDatePicked"
          />
        </v-menu>
      </section>

      <div class="plan-wizard__actions">
        <v-btn
          class="plan-wizard__create"
          color="primary"
          variant="flat"
          rounded="pill"
          :loading="generating"
          :disabled="!canCreate"
          prepend-icon="mdi-creation"
          @click="onCreate"
        >
          {{ t('marketing-agent.wizard.create') }}
        </v-btn>
      </div>
    </div>
  </v-card>
</template>

<script setup lang="ts">
import { useQuery } from '@vue/apollo-composable'
import { computed, inject, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import type { AuthInstance } from '@/auth'
import UpgradeInfoDialog from '@/components/UpgradeInfoDialog.vue'
import { useBranding } from '@/composables/useBranding'
import { useSocialMedia } from '@/composables/useSocialMedia'
import { SocialMediaType } from '@/generated/graphql'
import eventBus, { eventNames } from '@/lib/eventBus'
import { isLightUser } from '@/lib/productTypes'
import SocialMediaWrapper from '@/modules/connectionManager/socialMediaWrapper/index.vue'
import GET_ACTIVE_TOPICS from '@/modules/contentCreator/creator/queries/getActiveTopics.gql'
import { TrackingPlugin } from '@/plugins/tracking'

import { useRetryLimit } from './composables/useRetryLimit'
import { reportError } from './lib/reportError'
import RetryNotice from './RetryNotice.vue'

type TopicItem = {
  id: string
  name: string
  visibility?: { contentCreator?: boolean | null } | null
}

const props = defineProps({
  generating: {
    type: Boolean,
    default: false,
  },
  closable: {
    type: Boolean,
    default: false,
  },
  blockedUntil: {
    type: Date,
    default: null,
  },
  showPlanDuration: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits<{
  create: [payload: { topicId: string; startDate: string; windowDays: number }]
  close: []
}>()

const { t, locale } = useI18n()
const $auth = inject<AuthInstance>('auth')
const $tracking = inject<TrackingPlugin>('tracking')
const { isPlatformSmg, isPlatformTurkoglu } = useBranding()

const {
  socialMediaConnections,
  loadSocialMediaConnections,
  refetchSocialMediaConnections,
  canUseGoogleBusiness,
  isGoogleBusinessConnected,
  loadGoogleBusinessStatus,
} = useSocialMedia()

const {
  result: topicsResult,
  loading: topicsLoading,
  error: topicsError,
  refetch: refetchTopics,
} = useQuery(GET_ACTIVE_TOPICS)

// A failed load is not "no topics": show a retry instead of the create-topics hint.
const topicsRetry = useRetryLimit('load-topics', { error: topicsError })
const retryingTopics = ref(false)
watch(topicsError, (current, previous) => {
  if (current && !previous) reportError('load-topics', current)
})

async function onRetryTopics() {
  retryingTopics.value = true
  try {
    await topicsRetry.run(() => refetchTopics(), { operation: 'reload-topics' })
  } catch {
    // RetryNotice shows the next attempt, or the support hint.
  } finally {
    retryingTopics.value = false
  }
}

const topicId = ref<string | null>(null)
const windowDays = ref(28)
const lengthItems = computed(() => [
  { title: t('marketing-agent.wizard.one-week'), value: 7 },
  { title: t('marketing-agent.wizard.two-weeks'), value: 14 },
  { title: t('marketing-agent.wizard.four-weeks'), value: 28 },
])
const selectedDate = ref<Date | null>(null)
const showDatePicker = ref(false)
const connectionsLoaded = ref(false)
// A failed load would otherwise look like "nothing connected" and show the connect tiles.
const connectionsError = ref(false)
const connectionsRetry = useRetryLimit('load-connections')
const retryingConnections = ref(false)

const topicItems = computed(() =>
  ((topicsResult.value?.topics ?? []) as TopicItem[]).filter(
    (topic) => topic?.visibility?.contentCreator === true && topic.id && topic.name
  )
)

const preferredTopic = (topics: TopicItem[]) =>
  topics.find((topic) => /immobilienbewertung|property valuation/i.test(topic.name)) ?? topics[0]

watch(
  topicItems,
  (topics) => {
    if (!topics.length) {
      topicId.value = null
      return
    }
    if (topics.some((topic) => topic.id === topicId.value)) return
    topicId.value = preferredTopic(topics)?.id ?? null
  },
  { immediate: true }
)

const lightUser = computed(() => {
  const user = $auth?.user
  if (!user) return false
  return isLightUser(user)
})

const socialConnections = computed(() => [
  {
    id: 'linkedin',
    image: '/img/linkedin.png',
    provider: SocialMediaType.Linkedin,
    needsUpgrade: lightUser.value,
  },
  {
    id: 'instagram',
    image: '/img/instagram.png',
    provider: SocialMediaType.Instagram,
    tooltip: t('social-media.connect-instagram.info'),
    needsUpgrade: (isPlatformSmg.value || isPlatformTurkoglu.value) && lightUser.value,
  },
  {
    id: 'facebook',
    image: '/img/facebook.png',
    provider: SocialMediaType.Facebook,
  },
  ...(canUseGoogleBusiness.value
    ? [
        {
          id: 'google-business',
          image: '/img/kindTypes/google-business.svg',
          provider: SocialMediaType.GoogleBusiness,
        },
      ]
    : []),
])

const hasConnection = computed(() => {
  const connected = socialMediaConnections.value ?? []
  const postable = [
    SocialMediaType.Facebook,
    SocialMediaType.Instagram,
    SocialMediaType.Linkedin,
    SocialMediaType.GoogleBusiness,
  ]
  return (
    postable.some((provider) => connected.includes(provider)) || isGoogleBusinessConnected.value
  )
})

const showConnections = computed(
  () => connectionsLoaded.value && !connectionsError.value && !hasConnection.value
)

const minDate = computed(() => {
  const tomorrow = new Date()
  tomorrow.setHours(0, 0, 0, 0)
  tomorrow.setDate(tomorrow.getDate() + 1)
  if (!props.blockedUntil) return tomorrow
  const blocked = new Date(props.blockedUntil)
  blocked.setHours(0, 0, 0, 0)
  return blocked.getTime() > tomorrow.getTime() ? blocked : tomorrow
})

watch(
  minDate,
  (day) => {
    const next = new Date(day)
    next.setHours(0, 0, 0, 0)
    if (!selectedDate.value || selectedDate.value.getTime() < next.getTime()) {
      selectedDate.value = next
    }
  },
  { immediate: true }
)

const formattedDate = computed(() => {
  if (!selectedDate.value) return ''
  return selectedDate.value.toLocaleDateString(locale.value)
})

const canCreate = computed(
  () => !props.generating && hasConnection.value && !!topicId.value && !!selectedDate.value
)

function onDatePicked(value: Date | null) {
  if (!value) return
  const picked = new Date(value)
  picked.setHours(0, 0, 0, 0)
  if (picked.getTime() < minDate.value.getTime()) return
  selectedDate.value = picked
  showDatePicker.value = false
}

function onCreate() {
  if (!canCreate.value || !topicId.value || !selectedDate.value) return
  $tracking.event('Marketing Agent', 'Clicked', 'Create Plan')
  const year = selectedDate.value.getFullYear()
  const month = String(selectedDate.value.getMonth() + 1).padStart(2, '0')
  const day = String(selectedDate.value.getDate()).padStart(2, '0')
  // Noon UTC keeps the calendar day stable across company timezones.
  emit('create', {
    topicId: topicId.value,
    startDate: `${year}-${month}-${day}T12:00:00.000Z`,
    windowDays: windowDays.value,
  })
}

async function refreshConnections() {
  try {
    await refetchSocialMediaConnections()
    connectionsRetry.reset()
    connectionsError.value = false
  } catch (error) {
    reportError('refresh-connections', error)
    connectionsRetry.noteFailure()
    connectionsError.value = true
  }
  if (canUseGoogleBusiness.value) loadGoogleBusinessStatus()
}

async function onRetryConnections() {
  retryingConnections.value = true
  try {
    await connectionsRetry.run(() => refetchSocialMediaConnections(), {
      operation: 'reload-connections',
    })
    connectionsError.value = false
  } catch {
    // RetryNotice shows the next attempt, or the support hint.
  } finally {
    retryingConnections.value = false
  }
}

onMounted(async () => {
  try {
    await loadSocialMediaConnections()
    connectionsRetry.reset()
  } catch (error) {
    reportError('load-connections', error)
    connectionsRetry.noteFailure()
    connectionsError.value = true
  } finally {
    connectionsLoaded.value = true
  }
  if (canUseGoogleBusiness.value) loadGoogleBusinessStatus()
  eventBus.$on(eventNames.SOCIAL_MEDIA_UPDATED, refreshConnections)
})

onBeforeUnmount(() => {
  eventBus.$off(eventNames.SOCIAL_MEDIA_UPDATED, refreshConnections)
})
</script>

<style scoped>
.plan-wizard {
  overflow: hidden;
}

.plan-wizard__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  background: rgb(var(--v-theme-primary));
  font-size: 18px;
  font-weight: 600;
  line-height: 1.3;
}

.plan-wizard__body {
  padding: 22px 22px 18px;
  background: #fff;
}

.plan-wizard__section + .plan-wizard__section {
  margin-top: 18px;
}

.plan-wizard__label {
  margin: 0 0 8px;
  color: #1a1a1a;
  font-size: 15px;
  font-weight: 600;
}

.plan-wizard__tiles {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}

.plan-wizard__tiles--four {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.connect-tile-wrap {
  display: block;
  min-width: 0;
}

.connect-tile {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  min-height: 108px;
  padding: 12px 8px;
  border: 1px solid #d7e3df;
  border-radius: 10px;
  background: #fff;
  cursor: pointer;
}

.connect-tile:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.connect-tile__logo {
  max-width: 88px;
  max-height: 42px;
  object-fit: contain;
}

.connect-tile__action {
  color: rgb(var(--v-theme-primary));
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.plan-wizard__hint {
  margin: 8px 0 0;
  color: #5f6368;
  font-size: 13px;
}

.plan-wizard__actions {
  display: flex;
  justify-content: center;
  margin-top: 12px;
}

.plan-wizard__create {
  min-width: 180px;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}
</style>
